/**
 * Shared data-fetch helpers for the employee exports (single-profile PDF and
 * the directory Excel export).
 */
import { apiClient } from '@libs/apiRequest';
import { getCookie } from '@libs/cookies';
import { AUTH_TOKEN_KEY } from 'auth/common';

/** Employees fetched per request during a bulk export. */
const EXPORT_PAGE_SIZE = 25;
/** Hard stop for the bulk fetch loop (safety against a runaway loop). */
const EXPORT_MAX_PAGES = 100;

export const authHeaders = () => {
    const token = getCookie(AUTH_TOKEN_KEY);
    return token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : undefined;
};

export const fetchEmployee = async (email) => {
    const { data } = await apiClient.get(
        `employee/get?email=${encodeURIComponent(email)}`,
        authHeaders()
    );
    return data;
};

export const fetchCalendars = async () => {
    try {
        const {
            data: { data: calenders }
        } = await apiClient.get('calender/get/all', authHeaders());
        return calenders || [];
    } catch (e) {
        return [];
    }
};

export const fetchCalendarName = async (calenderId) => {
    if (!calenderId) return null;
    const calenders = await fetchCalendars();
    const found = calenders.find((c) => c.id === calenderId);
    return found ? found.name : calenderId;
};

/**
 * Employee documents (photograph, signature, NID, …) are stored as secured
 * backend paths (`file/private/*`) that require the Bearer token and per-file
 * permissions — a plain <img src> can NOT load them. Fetch the bytes through
 * the API (exactly like the leave-document viewer does) and hand back an
 * object URL. Returns null on any failure. Callers must revoke the URL.
 */
export const fetchSecureFileBlobUrl = async (path) => {
    if (!path || typeof path !== 'string') return null;
    try {
        const resp = await apiClient.get(path, {
            responseType: 'blob',
            ...(authHeaders() || {})
        });
        return window.URL.createObjectURL(resp.data);
    } catch (e) {
        return null;
    }
};

/**
 * A permanent, permission-safe link for a secured document: points at the
 * in-app /file-viewer page, which fetches the file through the API with the
 * viewer's own credentials. Never expires, never bypasses permissions.
 */
export const secureFileViewerLink = (path) =>
    path
        ? `${window.location.origin}/file-viewer?src=${encodeURIComponent(
              path
          )}`
        : '';

/**
 * Page through `employee/get/all/export` until every match is fetched.
 * Base set = active employees with enabled accounts; the two flags widen it.
 */
export const fetchEmployeesForExport = async (
    {
        filters = {},
        searchTerm,
        includeResigned = false,
        includeInactive = false
    },
    onProgress
) => {
    const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(
            ([, v]) => v !== undefined && v !== null && v !== ''
        )
    );

    const employees = [];
    let total = null;
    for (let page = 0; page < EXPORT_MAX_PAGES; page += 1) {
        const { data } = await apiClient.get('employee/get/all/export', {
            params: {
                limit: EXPORT_PAGE_SIZE,
                offset: page === 0 ? '' : String(page * EXPORT_PAGE_SIZE),
                ...(Object.keys(cleanFilters).length
                    ? { filters: cleanFilters }
                    : {}),
                ...(searchTerm ? { 'search-term': searchTerm } : {}),
                'include-resigned': includeResigned,
                'include-inactive': includeInactive
            },
            ...(authHeaders() || {})
        });
        total = parseInt(data?.total) || 0;
        const batch = data?.data || [];
        employees.push(...batch);
        onProgress && onProgress(Math.min(employees.length, total), total);
        if (!batch.length || employees.length >= total) break;
    }
    return { employees, total: total || employees.length };
};
